import React, { PropsWithChildren } from "react";
import {
  IconDictionaries,
  IconDictionaryItem,
  IconPickerState
} from "./IconPicker.types";

export const IconPickerContext = React.createContext<
  IconPickerState | undefined
>(undefined);

const ICONS_PER_PAGE = 50 as const;

export const IconPickerProvider: React.FC<
  PropsWithChildren<{
    onSearchValueChange?: (value: string) => void;
    dictionaries: IconDictionaries;
    initialIconSet?: string;
    customSets?: Record<string, IconDictionaryItem>;
  }>
> = ({ children, onSearchValueChange, initialIconSet, dictionaries }) => {
  const [page, setPage] = React.useState(1);
  const [availableIcons, setAvailableIcons] = React.useState<string[]>([]);
  const [totalIcons, setTotalIcons] = React.useState(0);
  // Guard against an initial set that isn't in the dictionaries (e.g.
  // "tactic" without the matching customSets) — fall back to the first
  // available set instead of crashing on `dictionaries[iconSet].keys`.
  const [iconSet, setIconSet] = React.useState<string>(() => {
    if (initialIconSet && dictionaries[initialIconSet]) return initialIconSet;
    return dictionaries.mdi ? "mdi" : Object.keys(dictionaries)[0] || "mdi";
  });

  const totalIconsPages = Math.ceil(totalIcons / ICONS_PER_PAGE);
  const [searchValue, setSearchValue] = React.useState("");
  const totalIconsToShown = ICONS_PER_PAGE * page;

  React.useEffect(() => {
    if (!dictionaries[iconSet]) {
      setTotalIcons(0);
      setAvailableIcons([]);
      return;
    }
    let filteredIcons = dictionaries[iconSet].keys;

    if (searchValue) {
      const lowerCaseSearchValue = searchValue.toLowerCase();
      filteredIcons = filteredIcons.filter((iconName) =>
        String(dictionaries[iconSet].icons[iconName])
          .toLowerCase()
          .includes(lowerCaseSearchValue)
      );
    }

    setTotalIcons(filteredIcons.length);
    setAvailableIcons(filteredIcons.slice(0, totalIconsToShown));
  }, [totalIconsToShown, searchValue, iconSet, dictionaries]);

  React.useEffect(() => {
    setPage(1);
    onSearchValueChange?.(searchValue);
  }, [searchValue]);

  return (
    <IconPickerContext.Provider
      value={{
        availableIcons,
        page,
        totalIconsPages,
        setPage,
        searchValue,
        setSearchValue,
        iconSet,
        setIconSet: (iconSet) => {
          setPage(1);
          setAvailableIcons([]);
          setTotalIcons(0);
          setIconSet(iconSet);
        },
        dictionaries
      }}
    >
      {children}
    </IconPickerContext.Provider>
  );
};
